import { Platform } from "react-native";

const EMBEDDED_MODAL_MAX_SCREEN_RATIO = 0.85;

export const IFRAME_LAYOUT_MESSAGE_TYPE = "VDB_IFRAME_LAYOUT";

export const isEmbeddedFrame = () =>
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  window.parent !== window;

// Inside an embedded frame, `vh` and `window.innerHeight` follow the iframe,
// which is as tall as the page. Modals must be capped to the device screen.
export const getEmbeddedModalMaxHeight = () => {
  if (!isEmbeddedFrame()) return 0;
  const systemHeight = window.screen?.availHeight || window.screen?.height || 0;
  return systemHeight
    ? Math.round(systemHeight * EMBEDDED_MODAL_MAX_SCREEN_RATIO)
    : 0;
};

export type IframeLayoutMode = "auto-height" | "internal-scroll";

export type IframeLayoutMessage = {
  type: typeof IFRAME_LAYOUT_MESSAGE_TYPE;
  mode: IframeLayoutMode;
  route: string;
  height?: number;
};

/**
 * Parent page contract:
 * - `auto-height`: set the iframe element height to `height` so the parent page scrolls.
 * - `internal-scroll`: restore the iframe's fixed height so this app scrolls inside the frame.
 * `"*"` matches the existing embed contract because customer parent origins vary.
 */
export const postIframeLayout = ({
  mode,
  route,
  height,
}: Omit<IframeLayoutMessage, "type">) => {
  if (Platform.OS !== "web" || typeof window === "undefined") return;
  if (window.parent === window) return;

  const message: IframeLayoutMessage = {
    type: IFRAME_LAYOUT_MESSAGE_TYPE,
    mode,
    route,
    ...(height != null ? { height } : {}),
  };

  window.parent.postMessage(message, "*");
};

// The dialog is centered in the tall iframe. Desktop browsers scroll the parent
// page to it on open. Mobile browsers do not, so the parent has to.
export const IFRAME_SCROLL_INTO_VIEW_MESSAGE_TYPE =
  "VDB_IFRAME_SCROLL_INTO_VIEW";

export const postIframeScrollIntoView = ({
  top,
  height,
}: {
  top: number;
  height: number;
}) => {
  if (!isEmbeddedFrame()) return;
  if (!Number.isFinite(top) || !Number.isFinite(height) || height <= 0) return;
  window.parent.postMessage(
    {
      type: IFRAME_SCROLL_INTO_VIEW_MESSAGE_TYPE,
      top: Math.round(top),
      height: Math.round(height),
    },
    "*"
  );
};
